// src/components/AutomationReport/ReportGenerator/ReportMain.js
import React, { useState, useEffect } from 'react';
import ReportForm from './ReportForm';
import DefectTable from './DefectTable';
import axios from 'axios';
import { Loader2, CheckCircle, UploadCloud, AlertCircle } from 'lucide-react';

const ReportMain = () => {
  const [formData, setFormData] = useState({});
  const [defects, setDefects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(''); // This will hold the displayed error message
  const [rawApiError, setRawApiError] = useState(null); // For detecting {"error": "..."} format

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const projectId = localStorage.getItem("selectedProject");
  const username = sessionStorage.getItem('username') || 'Guest';
  const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

  // Sync defect rows with Total Reject Quantity
  const handleRejectQtyChange = (qty) => {
    const quantity = qty || 0;
    setDefects(prev => {
      if (quantity > prev.length) {
        const toAdd = quantity - prev.length;
        return [...prev, ...Array(toAdd).fill().map(() => ({
          serial: '',
          description: '',
          observationDate: '',
          pictures: []
        }))];
      } else if (quantity < prev.length) {
        return prev.slice(0, quantity);
      }
      return prev;
    });
  };

  // Helper: Extract meaningful error message from API response
  const extractApiErrorMessage = (err) => {
    if (err.response?.data) {
      const data = err.response.data;

      // Case 1: JSON response with { error: "message" }
      if (data && typeof data === 'object' && data.error) {
        return data.error;
      }

      // Case 2: If it's a string that looks like JSON
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) return parsed.error;
        } catch {}
        return data.trim() || 'Server returned an error.';
      }
    }

    // Network or axios error
    if (err.message === 'Network Error') {
      return 'Network error. Please check your connection and try again.';
    }

    return err.message || 'An unexpected error occurred.';
  };

  // Clear error when user modifies form data or defects (helps UX)
  useEffect(() => {
    if (error) {
      setError('');
      setRawApiError(null);
    }
  }, [formData, defects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRawApiError(null);
    setSuccess(false);
    setUploadProgress('');
    setSubmitting(true);

    if (!projectId) {
      setError("No project selected.");
      setSubmitting(false);
      return;
    }
    if (defects.length === 0) {
      setError("Please enter Total Reject Quantity > 0.");
      setSubmitting(false);
      return;
    }

    const allComplete = defects.every(row =>
      row.serial?.trim() &&
      row.description?.trim() &&
      row.observationDate &&
      row.pictures.length > 0
    );
    if (!allComplete) {
      setError("All defect rows must have Serial, Description, Date, and at least 1 picture.");
      setSubmitting(false);
      return;
    }

    const basePayload = new FormData();
    basePayload.append('projectId', projectId);
    basePayload.append('initiator', username);
    Object.keys(formData).forEach(key => {
      if (formData[key]) basePayload.append(key, formData[key]);
    });

    const defectsJSON = JSON.stringify(
      defects.map(d => ({
        serial: d.serial.trim(),
        description: d.description.trim(),
        observationDate: d.observationDate,
        image_paths: []
      }))
    );
    basePayload.append('defects', defectsJSON);

    try {
      const response = await axios.post(`${baseURL}submit-ncr`, basePayload);

      const ncrId = response.data.ncrId;
      if (!ncrId) throw new Error("No NCR ID returned from server.");

      // Upload images in chunks
      for (let i = 0; i < defects.length; i++) {
        for (const file of defects[i].pictures) {
          await uploadFileInChunks(file, i, ncrId);
        }
      }

      setSuccess(true);
      alert("NCR Report + All Images Uploaded Successfully!");
      setFormData({});
      setDefects([]);
    } catch (err) {
      console.error("Submission failed:", err);

      // Extract and prioritize the backend {"error": "..."} message
      const apiErrorMsg = extractApiErrorMessage(err);
      setError(apiErrorMsg);
      setRawApiError(err.response?.data || null);
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  // Chunk upload function (unchanged)
  const uploadFileInChunks = async (file, defectIndex, ncrId) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const chunkPayload = new FormData();
      chunkPayload.append('ncrId', ncrId);
      chunkPayload.append('defectIndex', defectIndex);
      chunkPayload.append('chunkIndex', i);
      chunkPayload.append('totalChunks', totalChunks);
      chunkPayload.append('fileName', file.name);
      chunkPayload.append('fileChunk', chunk, file.name);

      setUploadProgress(`Uploading ${file.name} → Chunk ${i + 1}/${totalChunks}`);
      await axios.post(`${baseURL}submit-ncr`, chunkPayload, {
        timeout: 120000,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-10">
          Create New NCR Report
        </h1>

        <form onSubmit={handleSubmit} className="space-y-10">
          <ReportForm
            formData={formData}
            setFormData={setFormData}
            onRejectQtyChange={handleRejectQtyChange}
          />

          <DefectTable defects={defects} setDefects={setDefects} />

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="p-4 bg-blue-50 border border-blue-300 rounded-lg flex items-center gap-3">
              <UploadCloud className="w-6 h-6 text-blue-600 animate-pulse" />
              <span className="text-blue-800 font-medium">{uploadProgress}</span>
            </div>
          )}

          {/* Enhanced Error Display – Prioritizes API {"error": "..."} */}
          {error && (
            <div className="p-5 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-4">
              <AlertCircle className="w-7 h-7 text-red-600 flex-shrink-0" />
              <div>
                <strong className="text-red-800 text-lg">Submission Failed</strong>
                <p className="text-red-700 mt-2">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-5 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
              <div>
                <strong className="text-green-800 text-lg">Success!</strong>
                <p className="text-green-700 mt-1">
                  NCR Report and all images uploaded successfully.
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="text-center pt-8">
            <button
              type="submit"
              disabled={submitting || defects.length === 0}
              className={`px-12 py-4 text-lg font-bold text-white rounded-xl shadow-xl transition-all flex items-center gap-3 mx-auto ${
                submitting || defects.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin w-6 h-6" />
                  Uploading in Chunks...
                </>
              ) : (
                'Submit NCR Report'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportMain;